import { datadogLogs } from '@datadog/browser-logs';
import React, { ErrorInfo, ReactNode } from 'react';

import { Button, Icon } from '@column/column-ui-kit';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@column/ui';

interface ErrorBoundaryProps {
  children: ReactNode;
  resetOn?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  retryCount: number;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(): Pick<ErrorBoundaryState, 'hasError'> {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetOn !== this.props.resetOn) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Throwing here would escalate past this boundary and blank the page anyway.
    try {
      // Same name the window error listener uses, so existing monitors keep matching.
      datadogLogs.logger.error('Unhandled Exception', {
        error,
        message: error.message,
        source: 'error-boundary',
        componentStack: errorInfo.componentStack,
        translated: document.documentElement.className.includes('translated'),
        documentLang: document.documentElement.lang,
      });
    } catch {
      // ignored
    }
  }

  handleRetry = () => {
    this.setState((state) => ({ hasError: false, retryCount: state.retryCount + 1 }));
  };

  render() {
    if (!this.state.hasError) {
      return <React.Fragment key={this.state.retryCount}>{this.props.children}</React.Fragment>;
    }

    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
              <Icon.Warning />
            </EmptyMedia>
            <EmptyTitle>Something went wrong</EmptyTitle>
            <EmptyDescription>
              This page stopped responding. Trying again usually fixes it — your data has not been affected.
            </EmptyDescription>
          </EmptyHeader>
          <Button onClick={this.handleRetry}>Try again</Button>
        </Empty>
      </div>
    );
  }
}
